import { useParams, useNavigate, useLocation } from "react-router-dom";
import useShopSeo from "../../components/SEO/useShopSeo";
import { describeShop } from "../../components/SEO/shopSeo";
import usePublicShop from "../../hooks/usePublicShop";
import { Box, Typography, Button, Modal, IconButton } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Phone } from "@mui/icons-material";
import ShareIcon from "@mui/icons-material/Share";
import { useEffect, useState, useMemo, useRef } from "react";
import { ListingProductList } from "../../components";
import CloseIcon from "@mui/icons-material/Close";
import {
  GoogleMap,
  MarkerF,
  useJsApiLoader,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { useSelector } from "react-redux";
import { BASE_URL_TEST } from "../../utils/baseUrl";

const ListingShopDetails = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);
  const { slug } = useParams();
  // Extract the ID from the slug (assumes ID is after last hyphen)
  const id = slug ? slug.split("-").pop() : undefined;
  const { state } = useLocation();
  const navigate = useNavigate();

  const apptoken = useSelector((state) => state.user?.apptoken);
  const customerId = useSelector((state) => state.user?.customerId);
  const [shop, setShop] = useState(state?.shop || null);
  const [isVendorLoading, setIsVendorLoading] = useState(!state?.shop);
  const [vendorError, setVendorError] = useState(false);
  const [openMapModal, setOpenMapModal] = useState(false);
  const [directions, setDirections] = useState(null);

  const publicShop = usePublicShop(id);
  useShopSeo(publicShop);

  useEffect(() => {
    if (state?.shop || !id) {
      setIsVendorLoading(false);
      return;
    }

    const fetchVendor = async () => {
      setIsVendorLoading(true);
      setVendorError(false);

      try {
        const response = await fetch(
          `${BASE_URL_TEST}/api/vendor/findById/${id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: apptoken ? `Bearer ${apptoken}` : "",
            },
          },
        );

        if (!response.ok) {
          throw new Error("Unable to fetch shop details");
        }

        const responseData = await response.json();
        const vendor = responseData?.data || responseData?.vendor;

        if (!vendor) {
          throw new Error("Shop details were not returned");
        }

        const listingName = vendor.listing?.name || "";
        setShop({
          id: vendor.id,
          name: vendor.name,
          shopName: vendor.shop_name,
          location: vendor.location || vendor.city,
          city: vendor.city,
          listing: vendor.listing,
          listingName,
          category: listingName,
          description: listingName || "",
          image: vendor.store_image,
          distance: vendor.distance
            ? `${vendor.distance} kms near to your Location`
            : "N/A",
          rating: "4.8",
          address: `${vendor.street}, ${vendor.city}, ${vendor.state}, ${vendor.country}, ${vendor.pincode}`,
          street: vendor.street,
          state: vendor.state,
          pincode: vendor.pincode,
          openTime: vendor.open_time,
          closeTime: vendor.close_time,
          latitude: vendor.latitude,
          longitude: vendor.longitude,
          phoneNumber: vendor.phone_number,
          regularListType: vendor.reg_list_type,
        });
      } catch (error) {
        console.error("Failed to fetch listing shop:", error);
        setVendorError(true);
      } finally {
        setIsVendorLoading(false);
      }
    };

    fetchVendor();
  }, [id, state?.shop, apptoken]);

  const handleOpenMap = () => setOpenMapModal(true);
  const handleCloseMap = () => setOpenMapModal(false);
  const startLat = useSelector(
    (state) => state.user?.userLocation?.latitude ?? null,
  );

  const startLng = useSelector(
    (state) => state.user?.userLocation?.longitude ?? null,
  );

  useEffect(() => {
    const originLat = Number.parseFloat(startLat);
    const originLng = Number.parseFloat(startLng);
    const destinationLat = Number.parseFloat(shop?.latitude);
    const destinationLng = Number.parseFloat(shop?.longitude);

    if (
      !window.google ||
      !Number.isFinite(originLat) ||
      !Number.isFinite(originLng) ||
      !Number.isFinite(destinationLat) ||
      !Number.isFinite(destinationLng)
    ) {
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    const shopOrigin = {
      lat: originLat,
      lng: originLng,
    };
    const shopDestination = {
      lat: destinationLat,
      lng: destinationLng,
    };

    directionsService.route(
      {
        origin: shopOrigin,
        destination: shopDestination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
        } else if (status !== window.google.maps.DirectionsStatus.ZERO_RESULTS) {
          console.error("Directions error:", status);
        }
      },
    );
  }, [shop, startLat, startLng]);

  const pathCoordinates = useMemo(() => {
    const originLat = Number.parseFloat(startLat);
    const originLng = Number.parseFloat(startLng);
    const destinationLat = Number.parseFloat(shop?.latitude);
    const destinationLng = Number.parseFloat(shop?.longitude);

    if (
      Number.isFinite(originLat) &&
      Number.isFinite(originLng) &&
      Number.isFinite(destinationLat) &&
      Number.isFinite(destinationLng)
    ) {
      return [
        { lat: originLat, lng: originLng },
        { lat: destinationLat, lng: destinationLng },
      ];
    }
    return [];
  }, [startLat, startLng, shop?.latitude, shop?.longitude]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyA6CszWLFAJsbbDNtAtoXEWgWzqhU3pHB8",
  });

  const containerStyle = {
    width: "100%",
    height: "450px",
    borderRadius: "8px",
  };

  const mapRef = useRef(null);

  const onLoad = (map) => {
    mapRef.current = map;
  };

  useEffect(() => {
    if (
      openMapModal &&
      mapRef.current &&
      window.google &&
      window.google.maps?.event
    ) {
      window.google.maps.event.trigger(mapRef.current, "resize");
    }
  }, [openMapModal]);

  const handlePhone = () => {
    console.log("Button clicked");
    if (shop?.phoneNumber) {
      window.location.href = `tel:${shop.phoneNumber}`;
    } else {
      console.log("No phone number available");
    }
  };

  const handleShare = async () => {
    const url = window.location.href;

    if (navigator.share) {
      await navigator.share({
        title: shop.shopName,
        text: shop.description,
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
      alert("Link copied");
    }
  };

  if (isVendorLoading && !shop) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", marginTop: 4 }}>
        Loading shop details...
      </Typography>
    );
  }

  if (!shop || vendorError) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", marginTop: 4 }}>
        Shop not found
      </Typography>
    );
  }

  return (
    <Box sx={{ padding: 2, maxWidth: "1200px", margin: "0 auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ marginBottom: 2, fontSize: "1rem", textTransform: "none" }}
      >
        Back
      </Button>

      <Box
        component="img"
        src={shop.image}
        alt={shop.name}
        sx={{
          width: "100%",
          maxHeight: { xs: "200px", sm: "300px", md: "400px" },
          borderRadius: "8px",
          objectFit: "cover",
          fontSize: "1.5rem",
        }}
      />

      <Box sx={{ padding: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: "bold",
              textAlign: "left",
              fontSize: "1.75rem",
            }}
          >
            {shop.shopName} ({shop?.regularListType})
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <StarIcon sx={{ color: "#FFA500", marginRight: 0.5 }} />
            <Typography variant="body2" sx={{ color: "#FFA500" }}>
              {publicShop
                ? publicShop.rating
                  ? `${publicShop.rating.value} (${publicShop.rating.count})`
                  : "New"
                : ""}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            backgroundColor: "#FFF4E5",
            color: "#FF6F00",
            display: "inline-block",
            padding: "4px 8px",
            borderRadius: "4px",
            marginBottom: 2,
          }}
        >
          {shop.description}
        </Typography>

        <Typography variant="body1" sx={{ marginTop: 2 }}>
          Opening Timings:{" "}
          <strong>
            {shop.openTime} to {shop.closeTime}
          </strong>
        </Typography>
        {publicShop && (
          <Typography
            variant="body2"
            sx={{ marginTop: 2, color: "text.secondary" }}
          >
            {describeShop(publicShop)}
          </Typography>
        )}

        <Box sx={{ display: "flex", alignItems: "center", marginTop: 2 }}>
          <LocationOnIcon sx={{ color: "#FF6F00", marginRight: 1 }} />
          <Typography variant="body2">{shop.address}</Typography>
        </Box>

        <Box
          sx={{
            display: "flex",

            alignItems: "center",
            marginTop: 2,
          }}
        >
          <Typography variant="body2">{shop.distance} </Typography>
          {/* <Typography
            variant="body2"
            sx={{
              color: "#007BFF",
              textDecoration: "underline",
              cursor: "pointer",
              marginLeft: 2,
            }}
          >
            Distance: Dynamic
          </Typography> */}
        </Box>

        <Box
          sx={{
            display: "flex",
            marginTop: 3,
          }}
        >
          <Button
            variant="outlined"
            sx={{
              color: "#FF6F00",
              borderColor: "#FF6F00",
              "&:hover": {
                backgroundColor: "#FFF4E5",
                borderColor: "#FF6F00",
              },
              textTransform: "none",
            }}
            startIcon={<LocationOnIcon />}
            onClick={handleOpenMap}
          >
            Direction
          </Button>
          {customerId && (
            <Button
              variant="outlined"
              // onClick={handlePhone}
              startIcon={<Phone />}
              sx={{
                color: "#FF6F00",
                borderColor: "#FF6F00",
                "&:hover": {
                  backgroundColor: "#FFF4E5",
                  borderColor: "#FF6F00",
                },
                marginLeft: 2,
                textTransform: "none",
              }}
            >
              {shop?.phoneNumber}
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={handleShare}
            startIcon={<ShareIcon />}
            sx={{
              color: "#FF6F00",
              borderColor: "#FF6F00",
              marginLeft: 2,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#FFF4E5",
                borderColor: "#FF6F00",
              },
            }}
          >
            Share
          </Button>
        </Box>
      </Box>
      <ListingProductList shop={shop} />
      <Modal open={openMapModal} onClose={handleCloseMap}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "800px",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            outline: "none",
          }}
        >
          <IconButton
            onClick={handleCloseMap}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
              color: "#555",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Map to: {shop.name}
          </Typography>

          {isLoaded && pathCoordinates.length === 2 && (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={{
                lat: (pathCoordinates[0].lat + pathCoordinates[1].lat) / 2,
                lng: (pathCoordinates[0].lng + pathCoordinates[1].lng) / 2,
              }}
              zoom={13}
              onLoad={onLoad}
            >
              {directions && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    polylineOptions: {
                      strokeColor: "#FF0000",
                      strokeWeight: 4,
                    },
                  }}
                />
              )}

              <MarkerF position={pathCoordinates[0]} />
              <MarkerF position={pathCoordinates[1]} />
            </GoogleMap>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ListingShopDetails;
